import { Layout } from '@/components/Layout';
import { Link } from 'react-router-dom';

export function TermsPage() {
  return (
    <Layout>
      <div className="max-w-4xl mx-auto py-12 px-4">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Terms of Service</h1>
        <p className="text-gray-600 mb-8">Last updated: January 21, 2026</p>

        <div className="prose prose-blue max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Agreement to Terms</h2>
            <p className="text-gray-700 mb-4">
              By accessing or using Vistora ("Service"), operated by Vistora, Inc. ("Company", "we", "us", or "our"),
              you agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of these terms,
              you may not access the Service.
            </p>
            <p className="text-gray-700 mb-4">
              The Service is intended for users who are at least 18 years old. By using the Service, you represent and
              warrant that you are at least 18 years of age.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Description of Service</h2>
            <p className="text-gray-700 mb-4">
              Vistora provides a platform for creating, managing, and tracking Statements of Work (SOWs). The Service
              allows you to:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
              <li>Create and manage SOWs with versioned change tracking</li>
              <li>Share SOWs with clients via secure access tokens</li>
              <li>Track modifications and maintain audit trails</li>
              <li>Manage subscription plans and billing</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. User Accounts</h2>
            <p className="text-gray-700 mb-4">
              When you create an account with us, you must provide accurate, complete, and current information at all
              times. Failure to do so constitutes a breach of the Terms, which may result in immediate termination of
              your account.
            </p>
            <p className="text-gray-700 mb-4">
              You are responsible for safeguarding the password that you use to access the Service and for any activities
              or actions under your password. You agree not to disclose your password to any third party. You must
              notify us immediately upon becoming aware of any breach of security or unauthorized use of your account.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Subscription Plans and Billing</h2>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">4.1 Free and Paid Plans</h3>
            <p className="text-gray-700 mb-4">
              Vistora offers both free and paid subscription plans. Free plans are limited to 3 SOWs. Paid plans
              ("Pro" and "Enterprise") offer unlimited SOWs and additional features as described on our pricing page.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">4.2 Billing</h3>
            <p className="text-gray-700 mb-4">
              Paid subscriptions are billed on a recurring basis (monthly or annually) and will automatically renew
              unless canceled before the renewal date. Payment processing is handled by Stripe, Inc. By providing
              payment information, you authorize us to charge the applicable fees to your payment method.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">4.3 Refunds</h3>
            <p className="text-gray-700 mb-4">
              You may request a pro-rated refund within 14 days of your initial purchase or renewal. Refund requests
              must be submitted to <a href="mailto:support@vistora.app" className="text-blue-600 hover:underline">support@vistora.app</a>.
              After 14 days, all fees are non-refundable. We reserve the right to issue refunds or credits at our sole discretion.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">4.4 Cancellation</h3>
            <p className="text-gray-700 mb-4">
              You may cancel your subscription at any time through your account settings. Upon cancellation, your
              subscription will remain active until the end of your current billing period, after which you will be
              downgraded to the free plan. No refunds will be provided for the unused portion of your subscription
              after the 14-day refund period.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">4.5 Price Changes</h3>
            <p className="text-gray-700 mb-4">
              We reserve the right to modify our pricing structure. If we increase the price of your subscription,
              we will provide at least 30 days' notice before the change takes effect. Continued use of the Service
              after the price change constitutes your agreement to pay the modified amount.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Intellectual Property</h2>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">5.1 Your Content</h3>
            <p className="text-gray-700 mb-4">
              You retain all rights to the SOWs and content you create using the Service ("Your Content"). By using
              the Service, you grant us a limited, worldwide, non-exclusive, royalty-free license to host, store,
              and display Your Content solely for the purpose of providing the Service to you.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">5.2 Our IP</h3>
            <p className="text-gray-700 mb-4">
              The Service and its original content (excluding Your Content), features, and functionality are and will
              remain the exclusive property of Vistora, Inc. and its licensors. The Service is protected by copyright,
              trademark, and other laws. Our trademarks may not be used without our prior written permission.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Prohibited Uses</h2>
            <p className="text-gray-700 mb-4">
              You may not use the Service:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
              <li>For any unlawful purpose or to solicit others to perform unlawful acts</li>
              <li>To violate any international, federal, provincial, or state regulations, rules, laws, or local ordinances</li>
              <li>To infringe upon or violate our intellectual property rights or the intellectual property rights of others</li>
              <li>To harass, abuse, insult, harm, defame, slander, disparage, intimidate, or discriminate</li>
              <li>To submit false or misleading information</li>
              <li>To upload or transmit viruses or any other type of malicious code</li>
              <li>To spam, phish, pharm, pretext, spider, crawl, or scrape</li>
              <li>To interfere with or circumvent the security features of the Service</li>
              <li>To reverse engineer, decompile, or disassemble any part of the Service</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Data and Privacy</h2>
            <p className="text-gray-700 mb-4">
              Our collection and use of personal information is described in our{' '}
              <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link>.
              By using the Service, you consent to our collection and use of your data as described in the Privacy Policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Termination</h2>
            <p className="text-gray-700 mb-4">
              We may terminate or suspend your account immediately, without prior notice or liability, for any reason
              whatsoever, including without limitation if you breach the Terms. Upon termination, your right to use
              the Service will immediately cease.
            </p>
            <p className="text-gray-700 mb-4">
              All provisions of the Terms which by their nature should survive termination shall survive termination,
              including, without limitation, ownership provisions, warranty disclaimers, indemnity, and limitations of
              liability.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Limitation of Liability</h2>
            <p className="text-gray-700 mb-4">
              In no event shall Vistora, Inc., nor its directors, employees, partners, agents, suppliers, or affiliates,
              be liable for any indirect, incidental, special, consequential, or punitive damages, including without
              limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
              <li>Your access to or use of or inability to access or use the Service</li>
              <li>Any conduct or content of any third party on the Service</li>
              <li>Any content obtained from the Service</li>
              <li>Unauthorized access, use, or alteration of your transmissions or content</li>
            </ul>
            <p className="text-gray-700 mb-4">
              In no event will our total liability to you for all damages, losses, or causes of action exceed the
              amount you have paid us in the last six (6) months, or, if greater, one hundred dollars ($100).
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Disclaimer</h2>
            <p className="text-gray-700 mb-4">
              Your use of the Service is at your sole risk. The Service is provided on an "AS IS" and "AS AVAILABLE"
              basis. The Service is provided without warranties of any kind, whether express or implied, including,
              but not limited to, implied warranties of merchantability, fitness for a particular purpose,
              non-infringement, or course of performance.
            </p>
            <p className="text-gray-700 mb-4">
              Vistora, Inc., its subsidiaries, affiliates, and its licensors do not warrant that:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
              <li>The Service will function uninterrupted, secure, or available at any particular time or location</li>
              <li>Any errors or defects will be corrected</li>
              <li>The Service is free of viruses or other harmful components</li>
              <li>The results of using the Service will meet your requirements</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Governing Law</h2>
            <p className="text-gray-700 mb-4">
              These Terms shall be governed and construed in accordance with the laws of the State of Delaware,
              United States, without regard to its conflict of law provisions.
            </p>
            <p className="text-gray-700 mb-4">
              Our failure to enforce any right or provision of these Terms will not be considered a waiver of those
              rights. If any provision of these Terms is held to be invalid or unenforceable by a court, the remaining
              provisions of these Terms will remain in effect.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Changes to Terms</h2>
            <p className="text-gray-700 mb-4">
              We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision
              is material, we will try to provide at least 30 days' notice prior to any new terms taking effect. What
              constitutes a material change will be determined at our sole discretion.
            </p>
            <p className="text-gray-700 mb-4">
              By continuing to access or use our Service after those revisions become effective, you agree to be bound
              by the revised terms. If you do not agree to the new terms, please stop using the Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Contact Us</h2>
            <p className="text-gray-700 mb-4">
              If you have any questions about these Terms, please contact us:
            </p>
            <ul className="list-none text-gray-700 space-y-2">
              <li>By email: <a href="mailto:legal@vistora.app" className="text-blue-600 hover:underline">legal@vistora.app</a></li>
              <li>By mail: Vistora, Inc., 1111B S Governors Ave STE 98683, Dover, DE 19904</li>
            </ul>
          </section>
        </div>
      </div>
    </Layout>
  );
}
